// src/pages/AgentManagement/AgentList.tsx
// Table component to display agents list

import React from 'react';
import { Badge, Button, Spinner } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { IAgent } from 'slices/agentManagement/types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('agent_management');

interface AgentListProps {
  agents: IAgent[];
  loading: boolean;
  onEdit: (agent: IAgent) => void;
  page: number;
  pageSize: number;
}

// Trạng thái đại lý — mục 8.8, READE.md
const AGENT_STATUS_META: Record<string, { label: string; color: string }> = {
  Active: { label: st('dang_hoat_dong_label'), color: 'success' },
  Inactive: { label: st('ngung_hoat_dong_label'), color: 'secondary' },
  Locked: { label: st('tam_khoa_label'), color: 'danger' },
};

// Tap action-code cua cot "Thao tac" — dung de an han ca cot khi role hien tai khong co quyen
// voi bat ky action nao trong cot.
const AGENT_ACTION_COLUMN_CODES = ["button-edit"];

const AgentList: React.FC<AgentListProps> = ({ agents, loading, onEdit, page, pageSize }) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    AGENT_ACTION_COLUMN_CODES,
  );
  // Format commission rate: 0.15 → 15.00%
  const formatCommissionRate = (rate: number) => {
    //console.log(`${(rate * 100).toFixed(2)}%`);
    return `${(rate * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{st('dang_tai_du_lieu_text')}</p>
      </div>
    );
  }
  
  if (agents.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="ri-inbox-line display-4 text-muted"></i>
        <p className="mt-2 text-muted">{st('khong_co_du_lieu_text')}</p>
      </div>
    );
  }
  
  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" scope="col" style={{ width: 60 }}>{st('stt_column')}</th>
          <th scope="col" style={{ width: '10%' }}>{st('ma_dai_ly_column')}</th>
          <th scope="col">{st('ten_dai_ly_column')}</th>
          <th scope="col">{st('dia_chi_column')}</th>
          <th scope="col" style={{ width: '12%' }}>{st('so_dien_thoai_column')}</th>
          <th scope="col" style={{ width: '10%' }}>{st('ten_dang_nhap_column')}</th>
          <th scope="col">{st('email_column')}</th>
          <th scope="col" style={{ width: '10%' }}>{st('ty_le_hoa_hong_column')}</th>
          <th scope="col" style={{ width: '12%' }}>{st('trang_thai_column')}</th>
          {canShowActionColumn && (
          <th scope="col" style={{ width: '10%' }}>{st('thao_tac_column')}</th>
          )}
        </tr>
      }
    >
      {agents.map((agent, rowIndex) => (
        <tr key={agent.id}>
          <td className="text-center">{getSttNumber(page, pageSize, rowIndex)}</td>
          <td>
            <span className="fw-medium">{agent.code}</span>
          </td>
          <td>{agent.name}</td>
          <td>{agent.address}</td>
          <td>{agent.phoneNumber}</td>
          <td>{agent.username || <span className="text-muted">{st('chua_co_tai_khoan_text')}</span>}</td>
          <td>{agent.email || '-'}</td>
          <td>
            <span>
              {formatCommissionRate(agent.commissionRate)}
            </span>
          </td>
          <td>
            <Badge color={AGENT_STATUS_META[agent.status || 'Active']?.color || 'secondary'}>
              {AGENT_STATUS_META[agent.status || 'Active']?.label || agent.status}
            </Badge>
          </td>
          {canShowActionColumn && (
          <td>
            {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
            <Button
              color="primary"
              size="sm"
              data-action-code="button-edit"
              data-action-name="Sửa đại lý"
              onClick={() => onEdit(agent)}
              title={st('chinh_sua_title')}
            >
              <i className="ri-pencil-line"></i>
            </Button>
            )}
          </td>
          )}
        </tr>
      ))}
    </DataTableShell>
  );
};

export default AgentList;